import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Edit2, Trash2, Plus, MapPin, Calendar, Users } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { buildApiUrl } from "@/lib/config";

interface Package {
  id: number;
  title: string;
  description: string;
  duration: string;
  destination?: string;
  type?: string;
  price: string;
  image?: string;
  image_url?: string;
  imageUrl?: string;
  features?: string[];
  highlights?: string[];
  includes?: string[];
  featured: boolean;
  is_active: boolean;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export default function PackageManagement() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    duration: "",
    destination: "",
    type: "domestic",
    price: "",
    featured: false,
    highlights: [""],
    includes: [""]
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch packages
  const { data: packages = [], isLoading } = useQuery<Package[]>({
    queryKey: ["/api/packages"],
    queryFn: async () => {
      const response = await fetch(buildApiUrl('api/packages'));
      if (!response.ok) throw new Error('Failed to fetch packages');
      return response.json();
    },
  });

  // Create package mutation with Azure backend fix
  const createMutation = useMutation({
    mutationFn: async (packageData: { formData: FormData }) => {
      const token = localStorage.getItem('authToken');
      
      // First attempt: Try Azure backend
      try {
        const response = await fetch(buildApiUrl('api/packages'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: packageData.formData
        });
        
        if (response.ok) {
          return response.json();
        }
        
        // If Azure backend fails, try JSON payload instead of FormData
        if (response.status === 500) {
          console.warn('Azure backend FormData failed, retrying with JSON payload');
          
          // Convert FormData to JSON for Azure backend compatibility
          const jsonData: any = {};
          for (const [key, value] of packageData.formData.entries()) {
            if (key === 'highlights' || key === 'includes' || key === 'features') {
              try {
                jsonData[key] = JSON.parse(value as string);
              } catch {
                jsonData[key] = [];
              }
            } else if (key === 'featured' || key === 'is_active') {
              jsonData[key] = value === 'true';
            } else if (key !== 'image') { // Skip image for JSON payload
              jsonData[key] = value;
            }
          }
          
          // Add required Azure backend fields
          jsonData.features = jsonData.highlights || [];
          jsonData.is_active = true;
          jsonData.created_by = 'admin-001';
          jsonData.updated_by = 'admin-001';
          jsonData.image_url = null; // No image for JSON payload
          
          const jsonResponse = await fetch(buildApiUrl('api/packages'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(jsonData)
          });
          
          if (jsonResponse.ok) {
            return jsonResponse.json();
          }
        }
        
        // If both attempts fail, throw error with details
        const errorData = await response.text();
        console.error('Package creation error:', errorData);
        
        try {
          const errorJson = JSON.parse(errorData);
          throw new Error(errorJson.details || errorJson.message || errorJson.error || 'Azure backend package creation failed');
        } catch {
          throw new Error(`Azure backend error (${response.status}): ${errorData || 'Package creation failed'}`);
        }
        
      } catch (networkError) {
        console.error('Network error:', networkError);
        throw new Error('Network error: Unable to connect to Azure backend. Please check your connection.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/packages"] });
      setIsCreateOpen(false);
      resetForm();
      toast({
        title: "Success",
        description: "Package created successfully"
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Update package mutation
  const updateMutation = useMutation({
    mutationFn: async (packageData: { id: number; formData: FormData }) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(buildApiUrl(`api/packages/${packageData.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: packageData.formData
      });
      if (!response.ok) throw new Error('Failed to update package');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/packages"] });
      setEditingPackage(null);
      resetForm();
      toast({
        title: "Success",
        description: "Package updated successfully"
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Delete package mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(buildApiUrl(`api/packages/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      if (!response.ok) throw new Error('Failed to delete package');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/packages"] });
      toast({
        title: "Success",
        description: "Package deleted successfully"
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      duration: "",
      destination: "",
      type: "domestic",
      price: "",
      featured: false,
      highlights: [""],
      includes: [""]
    });
    setSelectedFile(null);
    setImagePreview("");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          title: "Error",
          description: "File size must be less than 5MB",
          variant: "destructive"
        });
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Error",
          description: "Please select an image file",
          variant: "destructive"
        });
        return;
      }
      
      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreate = () => {
    // Validate required fields
    if (!formData.title.trim() || !formData.description.trim() || !formData.duration.trim() || !formData.destination.trim() || !formData.price.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all required fields: title, description, duration, destination, and price",
        variant: "destructive"
      });
      return;
    }

    if (!selectedFile) {
      toast({
        title: "Error",
        description: "Please select an image file",
        variant: "destructive"
      });
      return;
    }

    const data = new FormData();
    data.append('title', formData.title);
    data.append('description', formData.description);
    data.append('duration', formData.duration);
    data.append('destination', formData.destination);
    data.append('type', formData.type);
    data.append('price', formData.price);
    data.append('featured', formData.featured.toString());
    data.append('highlights', JSON.stringify(formData.highlights.filter(h => h.trim())));
    data.append('includes', JSON.stringify(formData.includes.filter(i => i.trim())));
    
    // Add Azure backend-specific fields
    data.append('features', JSON.stringify(formData.highlights.filter(h => h.trim())));
    data.append('is_active', 'true');
    data.append('created_by', 'admin-001');
    data.append('updated_by', 'admin-001');
    
    data.append('image', selectedFile);

    createMutation.mutate({ formData: data });
  };

  const handleEdit = (pkg: Package) => {
    setEditingPackage(pkg);
    setFormData({
      title: pkg.title,
      description: pkg.description,
      duration: pkg.duration,
      destination: pkg.destination || "",
      type: pkg.type || "domestic",
      price: pkg.price.toString(),
      featured: pkg.featured,
      highlights: pkg.highlights || [""],
      includes: pkg.includes || [""]
    });
    // Use imageUrl field from API response for existing package images
    setImagePreview(pkg.imageUrl || pkg.image);
    setSelectedFile(null);
  };

  const handleUpdate = () => {
    if (editingPackage) {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('duration', formData.duration);
      data.append('destination', formData.destination);
      data.append('type', formData.type);
      data.append('price', formData.price);
      data.append('featured', formData.featured.toString());
      data.append('highlights', JSON.stringify(formData.highlights.filter(h => h.trim())));
      data.append('includes', JSON.stringify(formData.includes.filter(i => i.trim())));
      
      // Only append image if a new file is selected
      if (selectedFile) {
        data.append('image', selectedFile);
      }

      updateMutation.mutate({ id: editingPackage.id, formData: data });
    }
  };

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this package?')) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <CardTitle className="text-xl">Package Management</CardTitle>
              <CardDescription>
                Create, edit and manage travel packages
              </CardDescription>
            </div>
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button className="w-full sm:w-auto">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Package
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Package</DialogTitle>
                  <DialogDescription>
                    Add a new travel package to your offerings
                  </DialogDescription>
                </DialogHeader>
                <PackageForm
                  formData={formData}
                  setFormData={setFormData}
                  onSubmit={handleCreate}
                  onCancel={() => setIsCreateOpen(false)}
                  isLoading={createMutation.isPending}
                  selectedFile={selectedFile}
                  setSelectedFile={setSelectedFile}
                  imagePreview={imagePreview}
                  setImagePreview={setImagePreview}
                  handleFileChange={handleFileChange}
                />
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
      </Card>

      {/* Packages Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Current Packages</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Image</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Featured</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {packages.filter(pkg => pkg.isActive !== false).map((pkg) => (
                <TableRow key={pkg.id}>
                  <TableCell>
                    <img
                      src={pkg.image_url}
                      alt={pkg.title}
                      className="w-12 h-12 object-cover rounded border"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/fallback-image.png';
                      }}
                    />
                  </TableCell>
                  <TableCell className="align-middle font-medium">{pkg.title}</TableCell>
                  <TableCell className="align-middle text-gray-500 max-w-xs truncate">{pkg.description}</TableCell>
                  <TableCell className="align-middle">{pkg.duration || 'N/A'}</TableCell>
                  <TableCell className="align-middle font-mono">₹{pkg.price ? Number(pkg.price).toLocaleString() : 'N/A'}</TableCell>
                  <TableCell className="align-middle">
                    {pkg.featured ? (
                      <span className="px-2 py-1 rounded bg-orange-100 text-orange-800 text-xs">Featured</span>
                    ) : (
                      <span className="px-2 py-1 rounded bg-gray-100 text-gray-500 text-xs">-</span>
                    )}
                  </TableCell>
                  <TableCell className="align-middle">
                    <span className={`px-2 py-1 rounded text-xs ${pkg.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {pkg.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(pkg)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>Edit Package</DialogTitle>
                            <DialogDescription>
                              Update package information
                            </DialogDescription>
                          </DialogHeader>
                          <PackageForm
                            formData={formData}
                            setFormData={setFormData}
                            onSubmit={handleUpdate}
                            onCancel={() => setEditingPackage(null)}
                            isLoading={updateMutation.isPending}
                            selectedFile={selectedFile}
                            setSelectedFile={setSelectedFile}
                            imagePreview={imagePreview}
                            setImagePreview={setImagePreview}
                            handleFileChange={handleFileChange}
                          />
                        </DialogContent>
                      </Dialog>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(pkg.id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {packages.filter(pkg => pkg.isActive !== false).length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No packages available. Create your first package above.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PackageForm({
  formData,
  setFormData,
  onSubmit,
  onCancel,
  isLoading,
  selectedFile,
  setSelectedFile,
  imagePreview,
  setImagePreview,
  handleFileChange
}: {
  formData: any;
  setFormData: (data: any) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isLoading: boolean;
  selectedFile?: File | null;
  setSelectedFile?: (file: File | null) => void;
  imagePreview?: string;
  setImagePreview?: (preview: string) => void;
  handleFileChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const addHighlight = () => {
    setFormData({ ...formData, highlights: [...formData.highlights, ""] });
  };

  const removeHighlight = (index: number) => {
    const newHighlights = formData.highlights.filter((_, i) => i !== index);
    setFormData({ ...formData, highlights: newHighlights });
  };

  const updateHighlight = (index: number, value: string) => {
    const newHighlights = [...formData.highlights];
    newHighlights[index] = value;
    setFormData({ ...formData, highlights: newHighlights });
  };

  const addInclude = () => {
    setFormData({ ...formData, includes: [...formData.includes, ""] });
  };

  const removeInclude = (index: number) => {
    const newIncludes = formData.includes.filter((_, i) => i !== index);
    setFormData({ ...formData, includes: newIncludes });
  };

  const updateInclude = (index: number, value: string) => {
    const newIncludes = [...formData.includes];
    newIncludes[index] = value;
    setFormData({ ...formData, includes: newIncludes });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="title">Package Title *</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Goa Beach Paradise"
            required
          />
        </div>
        <div>
          <Label htmlFor="destination">Destination *</Label>
          <Input
            id="destination"
            value={formData.destination}
            onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
            placeholder="Goa, India"
            required
          />
        </div>
      </div>
      
      <div>
        <Label htmlFor="description">Description *</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="A wonderful beach vacation experience..."
          rows={3}
          required
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="duration">Duration *</Label>
          <Input
            id="duration"
            value={formData.duration}
            onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
            placeholder="5 Days 4 Nights"
            required
          />
        </div>
        <div>
          <Label htmlFor="type">Package Type *</Label>
          <select
            id="type"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
            required
          >
            <option value="domestic">Domestic</option>
            <option value="international">International</option>
          </select>
        </div>
        <div>
          <Label htmlFor="price">Price (INR) *</Label>
          <Input
            id="price"
            type="number"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
            placeholder="45000"
            required
          />
        </div>
      </div>

      {/* Package Highlights */}
      <div>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 gap-2">
          <Label>Package Highlights</Label>
          <Button type="button" size="sm" onClick={addHighlight} variant="outline" className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-1" />
            Add Highlight
          </Button>
        </div>
        {formData.highlights.map((highlight: string, index: number) => (
          <div key={index} className="flex flex-col sm:flex-row gap-2 mb-2">
            <Input
              value={highlight}
              onChange={(e) => updateHighlight(index, e.target.value)}
              placeholder="Beach activities, water sports, etc."
              className="flex-1"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => removeHighlight(index)}
              disabled={formData.highlights.length === 1}
              className="w-full sm:w-auto"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      {/* What's Included */}
      <div>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 gap-2">
          <Label>What's Included</Label>
          <Button type="button" size="sm" onClick={addInclude} variant="outline" className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-1" />
            Add Item
          </Button>
        </div>
        {formData.includes.map((item: string, index: number) => (
          <div key={index} className="flex flex-col sm:flex-row gap-2 mb-2">
            <Input
              value={item}
              onChange={(e) => updateInclude(index, e.target.value)}
              placeholder="Hotel accommodation, meals, transfers, etc."
              className="flex-1"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => removeInclude(index)}
              disabled={formData.includes.length === 1}
              className="w-full sm:w-auto"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      <div>
        <Label htmlFor="image">Package Image *</Label>
        <Input
          id="image"
          type="file"
          accept="image/*"
          onChange={handleFileChange || (() => {})}
          className="cursor-pointer"
          required={!imagePreview}
        />
        {imagePreview && (
          <div className="mt-2 space-y-2">
            <div className="flex items-center gap-3">
              <img 
                src={imagePreview} 
                alt="Package Preview" 
                className="w-32 h-24 object-cover rounded border"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  if (setImagePreview) setImagePreview("");
                  if (setSelectedFile) setSelectedFile(null);
                  // Clear the file input
                  const fileInput = document.getElementById('image') as HTMLInputElement;
                  if (fileInput) fileInput.value = '';
                }}
                className="self-start"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Remove Image
              </Button>
            </div>
          </div>
        )}
        <p className="text-xs text-gray-500 mt-1">
          Max file size: 5MB. Supported formats: JPG, PNG, GIF, WebP
        </p>
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="featured"
          checked={formData.featured}
          onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
          className="rounded"
        />
        <Label htmlFor="featured">Featured Package</Label>
      </div>

      <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2 pt-4">
        <Button variant="outline" onClick={onCancel} className="w-full sm:w-auto">
          Cancel
        </Button>
        <Button onClick={onSubmit} disabled={isLoading} className="w-full sm:w-auto">
          {isLoading ? "Saving..." : "Save Package"}
        </Button>
      </div>
    </div>
  );
}